// InviteLinkModal.tsx · v12.10.26 (#2133 Users › ⋮ › Copy invite link - the fresh one-time link, shown ONCE, masked, with Copy) · 2026-10-30 · VoiceSpectrum AI
// v12.10.26 (#2133, UI-PROTO-T191-INVITES v1.0): the server made a NEW link (the earlier one no longer works) and audited it. The link is kept
// in this dialog's state only - closing it forgets it ("make a new one instead"). Opened from userActions through showInviteLink(); the
// <InviteLinkHost /> on the Users page draws it.
import { useEffect, useState } from 'react';
import { Ic, ScModal, fmtDate } from './ui';

export interface InviteLinkInfo { name: string; link: string; expiresAt: string }
const EVENT = 'vs:invite-link';

/** v12.10.26 (#2133): open the shown-once dialog. */
export function showInviteLink(info: InviteLinkInfo): void {
  window.dispatchEvent(new CustomEvent<InviteLinkInfo>(EVENT, { detail: info }));
}

/** v12.10.26 (#2133): "https://api.voicespectrum.ai/r/serenalee/••••••" - the code part never shows on screen. */
export function maskInviteLink(link: string): string {
  const k = link.lastIndexOf('/');
  return k > 0 ? `${link.slice(0, k + 1)}••••••` : '••••••';
}

export function InviteLinkHost() {
  const [info, setInfo] = useState<InviteLinkInfo | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const on = (e: Event) => { setCopied(false); setInfo((e as CustomEvent<InviteLinkInfo>).detail ?? null); };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  if (!info) return null;
  const close = () => { setInfo(null); setCopied(false); };
  return (
    <ScModal onClose={close} label="Invite link" closeOnScrim={false}>
      <h4>Invite link · {info.name}</h4>
      <p><b>Shown once.</b> A new one-time link was made just now - the earlier link for this person no longer works. Close this and the link can’t be shown again (make a new one instead).</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '10px 0' }}>
        <code data-testid="invite-link-masked" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px 10px', border: '1px solid #d8dde8', borderRadius: 6, background: '#f7f8fb' }}>
          {maskInviteLink(info.link)}
        </code>
        <button type="button" className="ma" data-testid="invite-link-copy"
          onClick={() => { void navigator.clipboard?.writeText(info.link).then(() => setCopied(true)).catch(() => setCopied(false)); }}>
          <Ic k="copy" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="sc-mp" style={{ margin: '0 0 6px' }}>Valid 7 days{info.expiresAt ? ` - until ${fmtDate(info.expiresAt)}` : ''}. Send it to the person yourself (chat, phone call). One person, one use - it signs them in to set a password.</p>
      <p className="sc-mp" style={{ margin: 0, color: '#5b6680' }}>Recorded in the audit log: the link was made and shown, by whom and when.</p>
      <div className="mact"><button type="button" className="ma" onClick={close}>Done</button></div>
    </ScModal>
  );
}
